import { Accessor, For, JSX, ParentProps, Setter, Signal, createContext, createSignal, useContext } from "solid-js"
import style from './st-tab-group.module.css'
import { SetStoreFunction, createStore } from "solid-js/store"

type TabInfo = {
    id: string
    label: string | JSX.Element
}

export class TabGroupContext {
    selectedTabId: Accessor<string>
    setSelectedTabId: Setter<string>

    tabs: TabInfo[] = []
    private setTabs: SetStoreFunction<TabInfo[]>

    constructor(selectedTab?: Signal<string>) {
        [this.selectedTabId, this.setSelectedTabId] = selectedTab ?? createSignal('');
        [this.tabs, this.setTabs] = createStore<TabInfo[]>([]);
    }

    addTab(id: string, label: string | JSX.Element) {
        this.setTabs((tabs) => [...tabs, { id, label }])
    }

    removeTab(id: string) {
        this.setTabs((tabs) => tabs.filter(tab => tab.id !== id))
    }
}

const Context = createContext<TabGroupContext>()

export class TabGroupProps {
    id?: string
    selectedTabId?: Signal<string>
}

export default function STTabGroup(props: ParentProps<TabGroupProps>) {
    const tabGroup = new TabGroupContext(props.selectedTabId);

    return (
        <Context.Provider value={tabGroup}>
            <div class={style['tab-group']}>
                <nav>
                    <ul role='tablist'>
                        <For each={tabGroup.tabs}>{(tab) => (
                            <li role='presentation'>
                                <button role='tab'
                                        aria-selected={tabGroup.selectedTabId() === tab.id} onClick={() => tabGroup.setSelectedTabId(tab.id)}
                                        aria-controls={`panel-${tab.id}`}
                                        id={`tab-${tab.id}`}
                                        tabindex='0'
                                >
                                    {tab.label}
                                </button>
                            </li>
                        )}</For>
                    </ul>
                </nav>
                {props.children}
            </div>
        </Context.Provider>
    )
}

STTabGroup.defaultProps = new TabGroupProps()

export const useTabGroup = () => {
    const ctx = useContext(Context)
    if (!ctx) {
        throw new Error('TabGroupContext not found')
    }

    return ctx
}